import { api } from './api';
import type { KpiFilters } from './kpi.api';

// ----- Forecast -----------------------------------------------------------

export interface ForecastResponse {
  generatedAt: string;
  horizons: Array<{ days: number; weightedRevenue: number; opportunityCount: number }>;
}

// ----- User performance ---------------------------------------------------

export interface UserPerformanceRow {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  teamId: string | null;
  revenueSigned: number;
  signedCount: number;
  lostCount: number;
  createdCount: number;
  conversionRatePct: number;
  callsCount: number;
  meetingsCount: number;
  quotesCount: number;
}

export interface UserPerformanceResponse {
  period: { start: string; end: string };
  rows: UserPerformanceRow[];
}

// ----- Loss analysis ------------------------------------------------------

export interface LossAnalysisResponse {
  period: { start: string; end: string };
  byReason: Array<{ reason: string; count: number; totalPremiumHt: number }>;
  byMonth: Array<{ month: string; reason: string; count: number }>;
  /**
   * Série mois × pertes (toutes raisons confondues), zero-fill, avec
   * comparatif N-1. Conçue pour le chart `LossEvolutionCard`.
   */
  byMonthSeries: Array<{ month: string; count: number; previousYearCount: number }>;
  byContractType: Array<{ contractTypeId: string; code: string; label: string; count: number }>;
  byUser: Array<{ userId: string; firstName: string; lastName: string; count: number }>;
}

// ----- Signed revenue evolution -------------------------------------------

export interface SignedRevenueEvolutionResponse {
  period: { start: string; end: string };
  series: Array<{
    month: string;
    /** CA total signé (com + frais + honoraires). */
    current: number;
    currentCommission: number;
    currentFeesAndHonoraires: number;
    previousYear: number;
    previousYearCommission: number;
    previousYearFeesAndHonoraires: number;
  }>;
}

// ----- Cash-in evolution (trésorerie : encaissement à la prise d'effet) ---

export interface CashInEvolutionResponse {
  period: { start: string; end: string };
  series: Array<{
    month: string;
    current: number;
    currentCommission: number;
    currentFeesAndHonoraires: number;
    previousYear: number;
    previousYearCommission: number;
    previousYearFeesAndHonoraires: number;
  }>;
}

// ----- Company conversion -------------------------------------------------

export interface CompanyConversionResponse {
  period: { start: string; end: string };
  rows: Array<{
    companyId: string;
    name: string;
    quotesTotal: number;
    quotesSelected: number;
    quotesRejected: number;
    quotesPending: number;
    conversionRatePct: number;
    /** Enrichissement 2026-05-22 (demande LBC) : compagnies interrogées. */
    inquiriesTotal: number;
    inquiriesRefused: number;
    refusalRatePct: number;
  }>;
}

/** Stats globales inquiries (demande LBC 2026-05-22). */
export interface InquiriesStatsResponse {
  period: { start: string; end: string };
  opportunitiesTotal: number;
  inquiriesTotal: number;
  /** Moyenne nb compagnies interrogées par opp (sur opps ayant ≥1 inquiry). */
  avgInquiriesPerOpp: number;
  oppsWithInquiries: number;
  /** Taux de transformation interrogé → devis reçu sur la période. */
  transformationRatePct: number;
  refusalRatePct: number;
}

// ----- Contract type conversion -------------------------------------------

export interface ContractTypeConversionResponse {
  period: { start: string; end: string };
  rows: Array<{
    contractTypeId: string;
    code: string;
    label: string;
    opportunitiesTotal: number;
    opportunitiesWon: number;
    opportunitiesLost: number;
    conversionRatePct: number;
  }>;
}

// ----- Period comparison --------------------------------------------------

export interface PeriodComparisonResponse {
  current: {
    start: string;
    end: string;
    signedCount: number;
    createdCount: number;
    /** CA total signé (com + frais + honoraires) — décision Q6 2026-04-30. */
    revenueSigned: number;
    revenueSignedCommission: number;
    revenueSignedFeesAndHonoraires: number;
    /** Rétrocom totale cumulée des opps signées dans la période (3 taux apporteur). */
    retrocomsSigned: number;
    conversionRatePct: number;
  };
  previous: {
    start: string;
    end: string;
    signedCount: number;
    createdCount: number;
    revenueSigned: number;
    revenueSignedCommission: number;
    revenueSignedFeesAndHonoraires: number;
    retrocomsSigned: number;
    conversionRatePct: number;
  };
  delta: {
    signedCount: number | null;
    createdCount: number | null;
    revenueSigned: number | null;
    retrocomsSigned: number | null;
    conversionRatePct: number | null;
  };
}

/**
 * Top 10 apporteurs sur la période - cf SECURITY.md.
 * Affiché dans la card dédiée du tab Groupe LBC, à droite des graphes
 * Conversion par compagnie / par type de contrat.
 */
export interface TopApporteursResponse {
  period: { start: string; end: string };
  rows: Array<{
    apporteurId: string;
    /** Refonte polymorphe 2026-05-22 : `'person'` ou `'company'`. Pour
     *  `'company'`, `firstName`/`lastName` sont `null` et `companyName`
     *  porte la raison sociale. Utiliser `displayName()` de `@shared/apporteur`. */
    type: 'person' | 'company';
    firstName: string | null;
    lastName: string | null;
    /** Raison sociale (uniquement si `type='company'`). */
    companyName: string | null;
    /** Employeur (uniquement si `type='person'`, optionnel). */
    company: string | null;
    isActive: boolean;
    /** CA total généré (com + frais + honoraires). */
    caGenere: number;
    caGenereCommission: number;
    caGenereFeesAndHonoraires: number;
    /** Rétrocom totale (com + frais + hono via les 3 taux apporteur). */
    retrocoms: number;
    retroCommission: number;
    retroFees: number;
    retroHonoraires: number;
    cohortTotal: number;
    cohortWon: number;
    conversionRatePct: number | null;
  }>;
}

/**
 * Répartition par canal d'acquisition (origine) — demande client 2026-05-22.
 * Renvoie une ligne par origine + une ligne pour `origin=null` (opps sans
 * origine renseignée, ex. historiques ou import legacy).
 */
export interface RevenueByOriginResponse {
  period: { start: string; end: string };
  rows: Array<{
    /** Code enum OpportunityOrigin ou `null`. Le frontend mappe vers ORIGIN_VISUAL
     *  pour le libellé + couleur ; `null` est rendu « Non renseignée ». */
    origin:
      | 'campaign'
      | 'prospecting'
      | 'recommendation'
      | 'saturation'
      | 'inbound_call'
      | 'linkedin'
      | 'trade_show'
      | null;
    oppCount: number;
    signedCount: number;
    /** Σ weighted_total_revenue sur les opps CRÉÉES dans la période. */
    weightedRevenue: number;
  }>;
  total: {
    oppCount: number;
    signedCount: number;
    weightedRevenue: number;
  };
}

function qs(filters: Record<string, unknown>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === null || v === '') continue;
    p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

export const kpiDirectionApi = {
  forecast: (token: string, f: KpiFilters = {}) =>
    api.get<ForecastResponse>(`/api/v1/kpi/revenue-forecast${qs(f as Record<string, unknown>)}`, {
      accessToken: token,
    }),
  userPerformance: (token: string, f: KpiFilters = {}) =>
    api.get<UserPerformanceResponse>(
      `/api/v1/kpi/user-performance${qs(f as Record<string, unknown>)}`,
      { accessToken: token },
    ),
  lossAnalysis: (token: string, f: KpiFilters = {}) =>
    api.get<LossAnalysisResponse>(`/api/v1/kpi/loss-analysis${qs(f as Record<string, unknown>)}`, {
      accessToken: token,
    }),
  companyConversion: (token: string, f: KpiFilters = {}) =>
    api.get<CompanyConversionResponse>(
      `/api/v1/kpi/company-conversion${qs(f as Record<string, unknown>)}`,
      { accessToken: token },
    ),
  contractTypeConversion: (token: string, f: KpiFilters = {}) =>
    api.get<ContractTypeConversionResponse>(
      `/api/v1/kpi/contract-type-conversion${qs(f as Record<string, unknown>)}`,
      { accessToken: token },
    ),
  topApporteurs: (token: string, f: KpiFilters = {}) =>
    api.get<TopApporteursResponse>(
      `/api/v1/kpi/top-apporteurs${qs(f as Record<string, unknown>)}`,
      { accessToken: token },
    ),
  periodComparison: (token: string, f: KpiFilters = {}) =>
    api.get<PeriodComparisonResponse>(
      `/api/v1/kpi/period-comparison${qs(f as Record<string, unknown>)}`,
      { accessToken: token },
    ),
  signedRevenueEvolution: (token: string, f: KpiFilters = {}) =>
    api.get<SignedRevenueEvolutionResponse>(
      `/api/v1/kpi/signed-revenue-evolution${qs(f as Record<string, unknown>)}`,
      { accessToken: token },
    ),
  cashInEvolution: (token: string, f: KpiFilters = {}) =>
    api.get<CashInEvolutionResponse>(
      `/api/v1/kpi/cash-in-evolution${qs(f as Record<string, unknown>)}`,
      { accessToken: token },
    ),
  revenueByOrigin: (token: string, f: KpiFilters = {}) =>
    api.get<RevenueByOriginResponse>(
      `/api/v1/kpi/revenue-by-origin${qs(f as Record<string, unknown>)}`,
      { accessToken: token },
    ),
  inquiriesStats: (token: string, f: KpiFilters = {}) =>
    api.get<InquiriesStatsResponse>(
      `/api/v1/kpi/inquiries-stats${qs(f as Record<string, unknown>)}`,
      { accessToken: token },
    ),
};

export const LOSS_REASON_LABELS: Record<string, string> = {
  price: 'Prix trop élevé',
  coverage: 'Garanties insuffisantes',
  competitor: 'Concurrent retenu',
  proximity: 'Proximité / relation',
  timing: 'Timing',
  other: 'Autre',
  unknown: 'Non renseigné',
};
